import {
    Flag,
    LANGUAGE_INFO,
} from '@gagovictor/shared-frontend-core/dist/i18n';
import {
    THEME_MODES,
    themeIcon,
} from '@gagovictor/shared-frontend-core/dist/shared/components/ThemePicker';
import type { ThemeMode } from '@gagovictor/shared-frontend-core/dist/shared/redux/preferencesSlice';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';
import {
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Menu,
    MenuItem,
    Tooltip,
} from '@mui/material';
import React, { useState } from 'react';

import { Language, LANGUAGES, MessageKey, useI18n } from '../../i18n';

const THEME_KEYS: Record<ThemeMode, MessageKey> = {
    light: 'app.themeLight',
    dark: 'app.themeDark',
    system: 'app.themeSystem',
    daytime: 'app.themeDaytime',
};

interface Props {
    theme: ThemeMode;
    onThemeChange: (mode: ThemeMode) => void;
    /** Opens the timer settings dialog. */
    onOpenTimer: () => void;
}

/** Phone-sized toolbars have no room for the language and theme buttons, so they live under a gear. */
export const SettingsMenu: React.FC<Props> = ({
    theme,
    onThemeChange,
    onOpenTimer,
}) => {
    const { t, language, setLanguage } = useI18n();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const close = () => setAnchor(null);

    return (
        <>
            <Tooltip title={t('app.settings')}>
                <IconButton
                    color="inherit"
                    aria-label={t('app.settings')}
                    aria-haspopup="menu"
                    aria-expanded={anchor ? true : undefined}
                    onClick={(e) => setAnchor(e.currentTarget)}
                    data-testid="settings-menu"
                >
                    <SettingsRoundedIcon />
                </IconButton>
            </Tooltip>
            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
                <MenuItem
                    onClick={() => {
                        close();
                        onOpenTimer();
                    }}
                >
                    <ListItemIcon>
                        <TimerRoundedIcon />
                    </ListItemIcon>
                    <ListItemText>{t('settings.timer')}</ListItemText>
                </MenuItem>
                <Divider />
                <ListSubheader>{t('app.language')}</ListSubheader>
                {LANGUAGES.map((code: Language) => (
                    <MenuItem
                        key={code}
                        selected={code === language}
                        onClick={() => {
                            close();
                            if (code !== language) setLanguage(code);
                        }}
                    >
                        <ListItemIcon>
                            <Flag
                                country={LANGUAGE_INFO[code].country}
                                size={18}
                            />
                        </ListItemIcon>
                        <ListItemText>
                            {LANGUAGE_INFO[code].nativeName}
                        </ListItemText>
                    </MenuItem>
                ))}
                <Divider />
                <ListSubheader>{t('app.theme')}</ListSubheader>
                {THEME_MODES.map((mode) => (
                    <MenuItem
                        key={mode}
                        selected={mode === theme}
                        onClick={() => {
                            close();
                            onThemeChange(mode);
                        }}
                    >
                        <ListItemIcon>{themeIcon(mode)}</ListItemIcon>
                        <ListItemText>{t(THEME_KEYS[mode])}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};
